import {
  DndContext,
  DragOverlay,
  closestCenter,
  useDroppable,
  useDraggable,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import type { MouseEvent as ReactMouseEvent, ReactNode, PointerEvent as ReactPointerEvent } from 'react';
import { SplitRowDialog } from './SplitRowDialog';

type KanbanItem = { seq: number; item_external_id: string | null; location_code: string | null; qty_required: number };
type AssignmentPart = { operator_user_id: number; qty: number };
type Assignments = Record<number, AssignmentPart[]>; // seq → parts list

export interface ManualModeKanbanProps {
  items: KanbanItem[];
  operators: Array<{ id: number; label: string }>;
  value: Assignments;
  onChange: (next: Assignments) => void;
}

type DragKind =
  | { kind: 'unassigned'; seq: number }
  | { kind: 'assigned'; seq: number; op: number };

// Stable drag identifier so the handler can tell whether the user grabbed a
// row from the "Non Assegnato" column (`un-<seq>`) or the per-operator
// sub-card (`asn-<seq>-<opId>`). Note: kept distinct from the droppable
// column id `op-<opId>` so we never confuse the two namespaces.
function parseDragId(id: string): DragKind | null {
  if (id.startsWith('un-')) return { kind: 'unassigned', seq: Number(id.slice(3)) };
  if (id.startsWith('asn-')) {
    const [, seqStr, opStr] = id.split('-');
    return { kind: 'assigned', seq: Number(seqStr), op: Number(opStr) };
  }
  return null;
}

function sumQty(parts: AssignmentPart[] | undefined): number {
  return (parts ?? []).reduce((s, p) => s + p.qty, 0);
}

export function ManualModeKanban({ items, operators, value, onChange }: ManualModeKanbanProps) {
  const { t } = useTranslation('picklist');
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  // Multi-selection only applies to the "Non Assegnato" cards. Sub-cards in
  // operator columns are managed via drag (single-entry rows) or modal
  // (split rows) and are not part of the selection model.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [anchor, setAnchor] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [splitDialogSeq, setSplitDialogSeq] = useState<number | null>(null);

  function clearSelection(): void {
    setSelected(new Set());
    setAnchor(null);
  }

  function selectSingle(seq: number): void {
    setSelected(new Set([seq]));
    setAnchor(seq);
  }

  function toggleOne(seq: number): void {
    const next = new Set(selected);
    if (next.has(seq)) next.delete(seq);
    else next.add(seq);
    setSelected(next);
    setAnchor(seq);
  }

  function selectRange(seq: number): void {
    if (anchor === null) {
      toggleOne(seq);
      return;
    }
    const unassignedSeqs = items
      .filter((it) => sumQty(value[it.seq]) < it.qty_required)
      .map((it) => it.seq);
    if (!unassignedSeqs.includes(anchor) || !unassignedSeqs.includes(seq)) {
      toggleOne(seq);
      return;
    }
    const a = unassignedSeqs.indexOf(anchor);
    const b = unassignedSeqs.indexOf(seq);
    const [lo, hi] = a < b ? [a, b] : [b, a];
    const next = new Set(selected);
    for (const s of unassignedSeqs.slice(lo, hi + 1)) next.add(s);
    setSelected(next);
  }

  function handleDragStart(e: DragStartEvent): void {
    const id = String(e.active.id);
    setActiveId(id);
    const parsed = parseDragId(id);
    if (parsed?.kind === 'unassigned' && !selected.has(parsed.seq)) {
      // Dragging an unassigned card not in the current selection acts as a
      // single-row drag — drop the previous selection.
      clearSelection();
    }
  }

  function handleDragEnd(e: DragEndEvent): void {
    setActiveId(null);
    if (!e.over) return;
    const parsed = parseDragId(String(e.active.id));
    if (!parsed) return;
    const drop = String(e.over.id);

    if (parsed.kind === 'unassigned') {
      // Move one or more unassigned rows to a target column. When dropping on
      // an operator we append a part for the row's remaining qty.
      const seqs = selected.has(parsed.seq) ? Array.from(selected) : [parsed.seq];
      const next = { ...value };
      for (const seq of seqs) {
        const item = items.find((it) => it.seq === seq);
        if (!item) continue;
        const existing = next[seq] ?? [];
        const remaining = item.qty_required - sumQty(existing);
        if (remaining <= 0) continue;
        if (drop === 'unassigned') continue;
        const opId = Number(drop.replace('op-', ''));
        next[seq] = [...existing, { operator_user_id: opId, qty: remaining }];
      }
      onChange(next);
      clearSelection();
      return;
    }

    // Sub-card drag: only enabled for single-entry rows (whole-row case).
    const parts = value[parsed.seq] ?? [];
    if (parts.length !== 1 || parts[0].operator_user_id !== parsed.op) return;
    const next = { ...value };
    if (drop === 'unassigned') {
      delete next[parsed.seq];
    } else {
      const newOp = Number(drop.replace('op-', ''));
      next[parsed.seq] = [{ ...parts[0], operator_user_id: newOp }];
    }
    onChange(next);
  }

  // Esc deselezione + click sullo sfondo del kanban per uscire dalla selezione.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && selected.size > 0) clearSelection();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected.size]);

  function handleBackgroundClick(e: ReactMouseEvent): void {
    if (e.target === e.currentTarget && selected.size > 0) clearSelection();
  }

  const unassignedItems = items.filter((it) => sumQty(value[it.seq]) < it.qty_required);
  const splitItem = splitDialogSeq !== null ? items.find((it) => it.seq === splitDialogSeq) ?? null : null;

  // Drag overlay metadata
  const activeParsed = activeId ? parseDragId(activeId) : null;
  const groupDragCount = (activeParsed?.kind === 'unassigned' && selected.has(activeParsed.seq))
    ? selected.size
    : 1;
  const activeItem = activeParsed ? items.find((it) => it.seq === activeParsed.seq) ?? null : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      {selected.size > 0 && (
        <div
          role="status"
          aria-label={t('wizard.manual.selection_aria')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}
        >
          <span style={{ fontSize: 'var(--vp-text-sm)' }}>
            {selected.size === 1
              ? t('wizard.manual.selected_one')
              : t('wizard.manual.selected_other', { count: selected.size })}
          </span>
          <Button type="button" color="light" size="sm" onClick={clearSelection}>
            {t('wizard.manual.deselect')}
          </Button>
          <span style={{ fontSize: 'var(--vp-text-xs)', color: 'var(--vp-text-2)' }}>
            {t('wizard.manual.selection_hint')}
          </span>
        </div>
      )}
      <div
        onClick={handleBackgroundClick}
        style={{ display: 'grid', gridTemplateColumns: `repeat(${operators.length + 1}, 1fr)`, gap: 8 }}
      >
        <Column id="unassigned" title={t('wizard.manual.unassigned_column_count', { count: unassignedItems.length })}>
          {unassignedItems.map((it) => (
            <UnassignedCard
              key={it.seq}
              item={it}
              remaining={it.qty_required - sumQty(value[it.seq])}
              isSelected={selected.has(it.seq)}
              onSelectSingle={selectSingle}
              onToggle={toggleOne}
              onRange={selectRange}
              onSplit={() => setSplitDialogSeq(it.seq)}
            />
          ))}
        </Column>
        {operators.map((op) => {
          const opItems = items
            .map((it) => {
              const part = (value[it.seq] ?? []).find((p) => p.operator_user_id === op.id);
              if (!part) return null;
              const isWhole = (value[it.seq] ?? []).length === 1;
              return { it, part, isWhole };
            })
            .filter((x): x is { it: KanbanItem; part: AssignmentPart; isWhole: boolean } => x !== null);
          const totalQty = opItems.reduce((s, x) => s + x.part.qty, 0);
          return (
            <Column key={op.id} id={`op-${op.id}`} title={t('wizard.manual.operator_column', { label: op.label, count: opItems.length, qty: totalQty })}>
              {opItems.map(({ it, part, isWhole }) => (
                <AssignedCard
                  key={`${it.seq}-${op.id}`}
                  item={it}
                  opId={op.id}
                  qty={part.qty}
                  isWhole={isWhole}
                  onEditSplit={isWhole ? null : () => setSplitDialogSeq(it.seq)}
                />
              ))}
            </Column>
          );
        })}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          groupDragCount > 1 ? (
            <div
              style={{
                padding: 6,
                background: 'var(--vp-primary)',
                color: '#fff',
                borderRadius: 'var(--vp-r-xs)',
                fontWeight: 600,
                boxShadow: 'var(--vp-shadow-md)',
              }}
            >
              {t('wizard.manual.drag_group', { count: groupDragCount })}
            </div>
          ) : (
            <CardVisual
              label={activeItem.item_external_id ?? `seq-${activeItem.seq}`}
              location={activeItem.location_code}
              qty={activeParsed?.kind === 'unassigned'
                ? activeItem.qty_required - sumQty(value[activeItem.seq])
                : (value[activeItem.seq]?.[0]?.qty ?? activeItem.qty_required)}
              isSelected={false}
            />
          )
        ) : null}
      </DragOverlay>

      <SplitRowDialog
        isOpen={splitDialogSeq !== null}
        qtyTotal={splitItem?.qty_required ?? 0}
        itemLabel={splitItem?.item_external_id ?? (splitItem ? `seq-${splitItem.seq}` : '')}
        operators={operators}
        initialParts={splitDialogSeq !== null ? (value[splitDialogSeq] ?? []) : []}
        onSubmit={(parts) => {
          if (splitDialogSeq === null) return;
          const next = { ...value };
          if (parts.length === 0) delete next[splitDialogSeq];
          else next[splitDialogSeq] = parts;
          onChange(next);
          setSplitDialogSeq(null);
        }}
        onCancel={() => setSplitDialogSeq(null)}
      />
    </DndContext>
  );
}

function Column({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <section
      ref={setNodeRef}
      role="region"
      aria-label={title}
      style={{
        padding: 8,
        background: isOver ? 'var(--vp-primary-soft)' : 'var(--vp-surface)',
        border: '1px solid var(--vp-border)',
        borderRadius: 'var(--vp-r-sm)',
        minHeight: 120,
      }}
    >
      <h4 style={{ margin: '0 0 8px', fontSize: 'var(--vp-text-sm)' }}>{title}</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </section>
  );
}

interface UnassignedCardProps {
  item: KanbanItem;
  remaining: number;
  isSelected: boolean;
  onSelectSingle: (seq: number) => void;
  onToggle: (seq: number) => void;
  onRange: (seq: number) => void;
  onSplit: () => void;
}

function UnassignedCard({ item, remaining, isSelected, onSelectSingle, onToggle, onRange, onSplit }: UnassignedCardProps) {
  const { t } = useTranslation('picklist');
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `un-${item.seq}` });
  const { onPointerDown: dndPointerDown, ...restListeners } = (listeners ?? {}) as Record<string, ((e: ReactPointerEvent) => void) | undefined>;

  function handlePointerDown(e: ReactPointerEvent): void {
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      e.stopPropagation();
      if (e.shiftKey) onRange(item.seq);
      else onToggle(item.seq);
      return;
    }
    dndPointerDown?.(e);
  }

  function handleClick(e: ReactMouseEvent): void {
    if (e.ctrlKey || e.metaKey || e.shiftKey) return;
    e.stopPropagation();
    onSelectSingle(item.seq);
  }

  const partial = remaining < item.qty_required;
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...restListeners}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      aria-label={item.item_external_id ?? `seq-${item.seq}`}
      aria-pressed={isSelected}
      style={{
        padding: 6,
        background: isSelected ? 'var(--vp-primary-soft)' : 'var(--vp-bg)',
        border: `${isSelected ? 2 : 1}px solid ${isSelected ? 'var(--vp-primary)' : 'var(--vp-border)'}`,
        borderRadius: 'var(--vp-r-xs)',
        cursor: 'grab',
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, fontFamily: 'var(--vp-font-mono)' }}>{item.item_external_id ?? '—'}</div>
        {item.qty_required > 1 && (
          <button
            type="button"
            aria-label={t('wizard.manual.split_row_aria', { label: item.item_external_id ?? item.seq })}
            title={t('wizard.manual.split_row_tooltip')}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onSplit(); }}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 'var(--vp-text-sm)',
              padding: '0 4px',
            }}
          >
            ✂
          </button>
        )}
      </div>
      <div style={{ fontSize: 'var(--vp-text-xs)', color: 'var(--vp-text-2)' }}>
        {item.location_code ?? '—'} · {partial
          ? t('wizard.manual.qty_partial', { remaining, total: item.qty_required })
          : t('wizard.manual.qty_total', { qty: item.qty_required })}
      </div>
    </div>
  );
}

interface AssignedCardProps {
  item: KanbanItem;
  opId: number;
  qty: number;
  isWhole: boolean;                // single-entry assignment → draggable
  onEditSplit: (() => void) | null; // present when row is split (not draggable)
}

function AssignedCard({ item, opId, qty, isWhole, onEditSplit }: AssignedCardProps) {
  const { t } = useTranslation('picklist');
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `asn-${item.seq}-${opId}`,
    disabled: !isWhole,
  });
  return (
    <div
      ref={setNodeRef}
      {...(isWhole ? attributes : {})}
      {...(isWhole ? (listeners ?? {}) : {})}
      aria-label={item.item_external_id ?? `seq-${item.seq}`}
      style={{
        padding: 6,
        background: 'var(--vp-bg)',
        border: '1px solid var(--vp-border)',
        borderRadius: 'var(--vp-r-xs)',
        cursor: isWhole ? 'grab' : 'default',
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, fontFamily: 'var(--vp-font-mono)' }}>{item.item_external_id ?? '—'}</div>
        {!isWhole && onEditSplit && (
          <button
            type="button"
            aria-label={t('wizard.manual.edit_split_aria', { label: item.item_external_id ?? item.seq })}
            title={t('wizard.manual.edit_split_tooltip')}
            onClick={onEditSplit}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 'var(--vp-text-sm)',
              padding: '0 4px',
            }}
          >
            ✂
          </button>
        )}
      </div>
      <div style={{ fontSize: 'var(--vp-text-xs)', color: 'var(--vp-text-2)' }}>
        {item.location_code ?? '—'} · {t('wizard.manual.qty_total', { qty })}{!isWhole ? t('wizard.manual.split_suffix') : ''}
      </div>
    </div>
  );
}

interface CardVisualProps { label: string; location: string | null; qty: number; isSelected: boolean }
function CardVisual({ label, location, qty, isSelected }: CardVisualProps) {
  const { t } = useTranslation('picklist');
  return (
    <div
      style={{
        padding: 6,
        background: isSelected ? 'var(--vp-primary-soft)' : 'var(--vp-bg)',
        border: `${isSelected ? 2 : 1}px solid ${isSelected ? 'var(--vp-primary)' : 'var(--vp-border)'}`,
        borderRadius: 'var(--vp-r-xs)',
        boxShadow: 'var(--vp-shadow-md)',
      }}
    >
      <div style={{ fontFamily: 'var(--vp-font-mono)' }}>{label}</div>
      <div style={{ fontSize: 'var(--vp-text-xs)', color: 'var(--vp-text-2)' }}>
        {location ?? '—'} · {t('wizard.manual.qty_total', { qty })}
      </div>
    </div>
  );
}
